import { errorWords } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { RowActions } from "@/components/app/patterns/row-actions";
import { RecordCard } from "@/components/app/lists/record-card";
import { EmptyRows } from "@/components/app/lists/empty-rows";
import { RecordRow } from "@/components/app/lists/record-row";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle } from "@/lib/icons";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useFacility } from "@/lib/facility/facility";
import { orpc, type Output, type orpcClient } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import { EmailTemplateForm } from "./-components/messaging/email-template-form";
import { RowsSkeleton } from "@/routes/_authenticated/_administration/settings/-components/rows-skeleton";
import { OneBuildingCard } from "@/components/app/workspace/one-building-card";

export const Route = createFileRoute("/_authenticated/_administration/settings/email-templates")({
  beforeLoad: ({ context }) => guardRoute(context.me, "crm-email-templates"),
  component: EmailTemplateSettings,
});

type Template = Output<typeof orpcClient.crm.email.templates.list>[number];

const fail = (error: Error) => toast.error(errorWords(error));

/** The letters a building writes to a lead, ready to send from the lead's page. */
function EmailTemplateSettings() {
  const { facilityId } = useFacility();
  // A template belongs to one building, so adding one needs a building chosen.
  const realFacility = facilityId === "all" ? null : facilityId;
  const canManage = useCan("crm:manage");
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Template | null>(null);
  const [retiring, setRetiring] = useState<Template | null>(null);

  const templates = useQuery(
    orpc.crm.email.templates.list.queryOptions({
      // Switched-off templates still have to be visible, or nobody can switch
      // one back on.
      input: { facilityId, includeInactive: true },
    }),
  );
  const availability = useQuery(orpc.crm.email.availability.queryOptions());
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: orpc.crm.email.templates.key() });

  const create = useMutation(
    orpc.crm.email.templates.create.mutationOptions({
      onSuccess: async () => {
        toast.success("Template created");
        setAdding(false);
        await invalidate();
      },
      onError: fail,
    }),
  );
  const update = useMutation(
    orpc.crm.email.templates.update.mutationOptions({
      onSuccess: async () => {
        toast.success("Template updated");
        setEditing(null);
        await invalidate();
      },
      onError: fail,
    }),
  );
  const retire = useMutation(
    orpc.crm.email.templates.remove.mutationOptions({
      onSuccess: async () => {
        toast.success("Template switched off");
        setRetiring(null);
        await invalidate();
      },
      onError: fail,
    }),
  );

  // Active first, then by name: the list people write from, not an audit log.
  const rows = (templates.data ?? []).toSorted((a, b) =>
    a.isActive === b.isActive ? a.name.localeCompare(b.name) : a.isActive ? -1 : 1,
  );

  return (
    <>
      <PageHeader
        title="Email templates"
        meta="Emails staff send to a lead's family from the lead's page, with names filled in for them."
      />

      {availability.data && !availability.data.available ? (
        <Alert className="border-warn-border bg-warn-soft text-warn">
          <AlertTriangle aria-hidden />
          <AlertDescription className="text-foreground">
            Templates can be written and kept here, but nothing can be sent yet:{" "}
            {availability.data.reason}
          </AlertDescription>
        </Alert>
      ) : null}

      {canManage && !realFacility ? (
        <OneBuildingCard
          prompt={"Every building's templates are listed below. To add one, choose its building."}
        />
      ) : null}

      <RecordCard
        title="Templates"
        count={templates.data ? rows.length : undefined}
        canWrite={canManage && Boolean(realFacility)}
        addLabel="Add template"
        onAdd={() => setAdding(true)}
      >
        {templates.isPending ? (
          <RowsSkeleton />
        ) : templates.isError ? (
          <ReadFailed
            message="The templates could not be loaded."
            error={templates.error}
            retrying={templates.isFetching}
            onRetry={() => void templates.refetch()}
          />
        ) : rows.length === 0 ? (
          <EmptyRows
            text={
              realFacility
                ? "No email templates yet. Add one so staff can send a tour follow-up from any lead without retyping it."
                : "No email templates yet."
            }
          />
        ) : (
          <ul className="divide-y">
            {rows.map((template) => (
              <RecordRow
                key={template.id}
                title={
                  <span className="flex flex-wrap items-center gap-2">
                    {template.name}
                    {template.isActive ? null : <Badge variant="secondary">Off</Badge>}
                  </span>
                }
                meta={template.subject}
                actions={
                  canManage ? (
                    <RowActions
                      modal={false}
                      label={template.name}
                      primary={{ label: "Edit", onSelect: () => setEditing(template) }}
                      items={[
                        template.isActive
                          ? { label: "Switch off", onSelect: () => setRetiring(template) }
                          : {
                              label: "Switch on",
                              onSelect: () =>
                                update.mutate({ templateId: template.id, isActive: true }),
                            },
                      ]}
                    />
                  ) : null
                }
              />
            ))}
          </ul>
        )}
      </RecordCard>

      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add template</DialogTitle>
          </DialogHeader>
          <EmailTemplateForm
            pending={create.isPending}
            submitLabel="Add template"
            onCancel={() => setAdding(false)}
            onSubmit={(values) => {
              if (!realFacility) return;
              create.mutate({ ...values, facilityId: realFacility });
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={editing !== null} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit template</DialogTitle>
          </DialogHeader>
          {editing ? (
            <EmailTemplateForm
              key={editing.id}
              pending={update.isPending}
              defaultValues={{
                name: editing.name,
                subject: editing.subject,
                body: editing.body,
              }}
              onCancel={() => setEditing(null)}
              onSubmit={(values) => update.mutate({ ...values, templateId: editing.id })}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <AlertDialog open={retiring !== null} onOpenChange={(open) => !open && setRetiring(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Switch off “{retiring?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              It stops being offered when somebody writes to a lead. Emails already sent are not
              affected, and it can be switched back on from this list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => retiring && retire.mutate({ templateId: retiring.id })}
            >
              Switch off template
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
